import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { MessageCircle, Copy, Check, Search, Sparkles, Loader2 } from "lucide-react";
import { api, rp, statusColor } from "@/lib/api";
import { Input } from "@/components/ui/input";

export default function WhatsAppHub() {
  const [day, setDay] = useState(new Date().toISOString().slice(0, 10));
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [tab, setTab] = useState("pickup");

  const load = useCallback(async () => {
    const params = q.trim() ? { q: q.trim() } : { date_from: day, date_to: day };
    const r = await api.get("/bookings", { params });
    setRows(r.data.filter((b) => tab === "pickup"
      ? b.booking_status === "Confirmed"
      : b.booking_status !== "Cancelled"));
  }, [day, q, tab]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="admin-whatsapp">
      <h1 className="font-display text-3xl tracking-tight">WhatsApp Center</h1>
      <p className="mt-1 text-sm text-ink-soft">
        Semua template chat untuk satu booking ada di sini — pengingat, sebelum/sesudah antar, sebelum/sesudah
        pengembalian, kendala unit, refund deposit, dan kirim foto. Edit isi template di{" "}
        <Link to="/admin/settings" className="text-lm-red">Settings → WhatsApp Message Templates</Link>.
      </p>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <button data-testid="wa-tab-pickup" onClick={() => setTab("pickup")}
          className={`h-11 px-4 rounded-md text-sm ${tab === "pickup" ? "bg-ink text-cream" : "border border-black/15"}`}>
          Pickup hari ini
        </button>
        <button data-testid="wa-tab-all" onClick={() => setTab("all")}
          className={`h-11 px-4 rounded-md text-sm ${tab === "all" ? "bg-ink text-cream" : "border border-black/15"}`}>
          Semua template
        </button>
        <input data-testid="wa-date" type="date" value={day} onChange={(e) => { setQ(""); setDay(e.target.value); }}
          className="h-11 rounded-md border border-input bg-white px-3 text-sm" />
        <button data-testid="wa-today" onClick={() => { setQ(""); setDay(new Date().toISOString().slice(0, 10)); }}
          className="h-11 px-4 rounded-md border border-black/15 text-sm">Hari ini</button>
        <div className="flex items-center gap-2 ml-auto">
          <Input data-testid="wa-search" className="w-56" placeholder="Cari kode / nama / WA"
            value={q} onChange={(e) => setQ(e.target.value)} />
          <button onClick={load} className="h-11 px-4 rounded-md border border-black/15 text-sm inline-flex items-center gap-1">
            <Search size={14} /> Cari</button>
        </div>
      </div>

      <div className="mt-5 space-y-3">
        {rows.map((b) => (
          <div key={b.id} className="bg-white border border-black/10 rounded-lg p-4" data-testid={`wa-booking-${b.booking_code}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="text-sm font-semibold">{b.customer_name} · {b.customer_whatsapp}</div>
                <div className="text-xs text-ink-soft mt-0.5">
                  <Link to={`/admin/bookings/${b.id}`} className="text-lm-red">{b.booking_code}</Link>
                  {" "}· {b.rental_date} {b.start_time} · {b.package_name_snapshot} · {b.quantity} unit · {rp(b.total_payment)}
                </div>
                <span className={`mt-2 inline-block text-[11px] px-2 py-0.5 rounded-full border ${statusColor(b.booking_status)}`}>{b.booking_status}</span>
                {b.is_overdue && <span data-testid={`wa-overdue-${b.booking_code}`} className="mt-2 ml-2 inline-block text-[11px] px-2 py-0.5 rounded-full bg-lm-red text-white">Lewat batas waktu</span>}
              </div>
              <button data-testid={`wa-open-${b.booking_code}`} onClick={() => setOpenId(openId === b.id ? null : b.id)}
                className="lm-btn-primary rounded-md px-4 py-2.5 text-sm font-semibold inline-flex items-center gap-2">
                <MessageCircle size={15} /> {openId === b.id ? "Tutup" : "Buka Chat Center"}
              </button>
              {tab === "pickup" && (
                <a data-testid={`wa-pickup-${b.booking_code}`} target="_blank" rel="noreferrer"
                  href={`https://wa.me/${(b.customer_whatsapp || "").replace(/\D/g, "").replace(/^0/, "62")}?text=${encodeURIComponent(`Halo ${b.customer_name}, pesanan Portable Mahjong Anda sudah di-pickup oleh kurir. Kami akan melampirkan foto pickup di chat ini. Terima kasih.`)}`}
                  className="rounded-md border border-emerald-300 px-4 py-2.5 text-sm text-emerald-800">
                  WA pickup
                </a>
              )}
            </div>
            {openId === b.id && <MessageBox booking={b} reload={load} />}
          </div>
        ))}
        {rows.length === 0 && (
          <div className="bg-white border border-black/10 rounded-lg p-5 text-sm text-ink-soft" data-testid="wa-empty">
            Tidak ada booking pada tanggal / pencarian ini.
          </div>
        )}
      </div>
    </div>
  );
}

const MessageBox = ({ booking, reload }) => {
  const [data, setData] = useState(null);
  const [active, setActive] = useState(null);
  const [copied, setCopied] = useState("");
  const [edited, setEdited] = useState(null);
  const [intent, setIntent] = useState("");
  const [aiBusy, setAiBusy] = useState(false);

  useEffect(() => {
    api.get(`/bookings/${booking.id}/messages`).then((r) => { setData(r.data); setActive(r.data.suggested_key); });
  }, [booking.id]);
  if (!data) return <div className="mt-3 text-sm text-ink-soft">Memuat template…</div>;

  const current = data.messages.find((m) => m.key === active) || data.messages[0];
  const body = edited ?? current.message;
  const waHref = `https://wa.me/${(booking.customer_whatsapp || "").replace(/\D/g, "").replace(/^0/, "62")}?text=${encodeURIComponent(body)}`;
  const send = async () => {
    window.open(waHref, "_blank");
    if (current.key === "reminder" && !edited) { await api.post(`/bookings/${booking.id}/reminder-sent`); reload(); }
    toast.success("Dibuka di WhatsApp");
  };
  const copy = async () => {
    await navigator.clipboard.writeText(body);
    setCopied(current.key); toast.success("Teks dicopy");
  };
  const draftWithAi = async () => {
    if (!intent.trim()) return toast.error("Tulis dulu maksud pesannya");
    setAiBusy(true);
    try {
      const r = await api.post("/ai/wa-draft", { booking_id: booking.id, intent, base_message: current.message });
      setEdited(r.data.message); toast.success("Draft AI siap — silakan cek dulu");
    } catch (e) { toast.error(e?.response?.data?.detail || "AI gagal membuat pesan"); }
    finally { setAiBusy(false); }
  };

  return (
    <div className="mt-4 border-t border-black/5 pt-3" data-testid="wa-box">
      <p className="text-xs text-ink-soft" data-testid="wa-suggestion">
        Disarankan untuk status <span className="font-semibold">{booking.booking_status}</span>:{" "}
        <span className="text-lm-red font-semibold">{data.messages.find((m) => m.key === data.suggested_key)?.label}</span>
        {data.is_overdue && <span className="ml-2 text-lm-red">· LEWAT BATAS WAKTU</span>}
      </p>
      <div className="mt-2 flex flex-wrap gap-2">
        {data.messages.map((m) => (
          <button key={m.key} data-testid={`wa-tpl-${m.key}`} onClick={() => { setActive(m.key); setEdited(null); }}
            className={`text-xs px-3 py-2 rounded-full border ${active === m.key ? "bg-ink text-cream border-ink" : "border-black/15"}`}>
            {m.label}{m.key === data.suggested_key ? " ★" : ""}
          </button>
        ))}
      </div>
      <textarea data-testid="wa-preview" rows={Math.min(16, body.split("\n").length + 2)} value={body}
        onChange={(e) => setEdited(e.target.value)}
        className="mt-3 w-full whitespace-pre-wrap font-sans text-xs text-ink bg-cream/60 rounded-md p-3 border border-black/10" />

      <div className="mt-3 rounded-md border border-lm-gold/40 bg-lm-gold/5 p-3">
        <div className="text-xs font-semibold flex items-center gap-1.5"><Sparkles size={14} className="text-lm-gold" /> Tulis ulang dengan AI</div>
        <div className="mt-2 flex flex-col sm:flex-row gap-2">
          <input data-testid="ai-intent" value={intent} onChange={(e) => setIntent(e.target.value)}
            placeholder="Contoh: minta maaf driver telat 30 menit, tawarkan kompensasi kecil"
            className="flex-1 h-10 rounded-md border border-black/15 bg-white px-3 text-xs" />
          <button data-testid="ai-draft-btn" onClick={draftWithAi} disabled={aiBusy}
            className="h-10 px-4 rounded-md bg-ink text-cream text-xs font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60">
            {aiBusy ? <Loader2 className="animate-spin" size={14} /> : <Sparkles size={14} />} Buat Draft
          </button>
        </div>
        {edited !== null && (
          <button data-testid="ai-reset-draft" onClick={() => setEdited(null)} className="mt-2 text-xs text-lm-red">
            Kembalikan ke template asli
          </button>
        )}
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <button data-testid="wa-send" onClick={send}
          className="lm-btn-primary rounded-md px-4 py-2.5 text-sm font-semibold inline-flex items-center gap-2">
          <MessageCircle size={15} /> Kirim via WhatsApp
        </button>
        <button data-testid="wa-copy" onClick={copy}
          className="rounded-md px-4 py-2.5 text-sm border border-black/15 inline-flex items-center gap-2">
          {copied === current.key ? <Check size={15} /> : <Copy size={15} />} Copy teks
        </button>
      </div>
    </div>
  );
};
